import { DEFAULT_STREAM_RESPONSE_DEADLINE, DEFAULT_STREAM_STALL_CHECK_INTERVAL, MAX_STREAM_RESPONSE_TIME_CEILING, SwChannelMessageTypes, } from '../../constants';
import { DEFAULT_PING_INTERVAL } from '../ping-manager';
const toAbortError = (reason) => reason instanceof Error ? reason : new Error(String(reason));
// A plain deadline: the op gets one fixed window to settle in.
export const sendWithTimeout = async (op, { timeout = DEFAULT_STREAM_RESPONSE_DEADLINE } = {}) => {
    let timeoutId;
    try {
        return await Promise.race([
            op(),
            new Promise((_, reject) => {
                timeoutId = window.setTimeout(() => {
                    reject(new Error(`Operation timed out after ${timeout}ms`));
                }, timeout);
            }),
        ]);
    }
    finally {
        clearTimeout(timeoutId);
    }
};
export const sendWithStallCheck = async (op, { isRecipientAlive, maxWait = MAX_STREAM_RESPONSE_TIME_CEILING, signal, stallCheckInterval = DEFAULT_STREAM_STALL_CHECK_INTERVAL, }) => {
    let timeoutId;
    let abortListener;
    try {
        await Promise.race([
            op(),
            new Promise((_, reject) => {
                const startTimestamp = Date.now();
                const checkForStall = () => {
                    const elapsed = Date.now() - startTimestamp;
                    const remaining = maxWait - elapsed;
                    if (isRecipientAlive() && remaining > 0) {
                        timeoutId = window.setTimeout(checkForStall, Math.min(stallCheckInterval, remaining));
                        return;
                    }
                    reject(new Error(`Operation timed out after ${elapsed}ms`));
                };
                timeoutId = window.setTimeout(checkForStall, Math.min(stallCheckInterval, maxWait));
            }),
            ...(signal
                ? [
                    new Promise((_, reject) => {
                        const rejectAsAborted = () => reject(toAbortError(signal.reason));
                        if (signal.aborted) {
                            rejectAsAborted();
                            return;
                        }
                        abortListener = rejectAsAborted;
                        signal.addEventListener('abort', abortListener, {
                            once: true,
                        });
                    }),
                ]
                : []),
        ]);
    }
    finally {
        clearTimeout(timeoutId);
        if (abortListener) {
            signal?.removeEventListener('abort', abortListener);
        }
    }
};
export const postMessageToSwOnSwChannel = (serviceWorker, message, transfer) => {
    const { active } = serviceWorker;
    if (!active) {
        throw new Error('Service Worker registration does not have an active worker');
    }
    active.postMessage(message, transfer ?? []);
};
// Each send allocates a fresh MessageChannel so the SW can ack only this
// specific send. The ack signals that the SW's Response stream actually
// consumed the message — which is the load-bearing watchdog signal for
// Safari (Safari does not cancel SW streams when downloads are paused).
export const sendWithAck = (serviceWorker, message, timeoutOptions) => {
    const channel = new MessageChannel();
    postMessageToSwOnSwChannel(serviceWorker, message, [channel.port2]);
    const ack = new Promise((res) => {
        channel.port1.onmessage = (event) => res(event.data);
    });
    return (timeoutOptions ? sendWithTimeout(() => ack, timeoutOptions) : ack).finally(() => {
        channel.port1.onmessage = null;
        channel.port1.close();
    });
};
export const sendChunkThroughSwInternalChannel = (serviceWorker) => {
    return {
        sendChunk: (chunk) => sendWithAck(serviceWorker, [
            SwChannelMessageTypes.CHUNK,
            { chunk },
        ]),
        sendEnd: () => sendWithAck(serviceWorker, [SwChannelMessageTypes.END]),
    };
};
export const pingThroughSwInternalChannel = async (serviceWorker, onPong) => {
    try {
        const ack = await sendWithAck(serviceWorker, [SwChannelMessageTypes.PING], { timeout: DEFAULT_PING_INTERVAL });
        onPong(ack);
    }
    catch {
    }
};
